import React, { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Card, Row, Col, Typography, Input, Button, Spin, theme } from "antd";
import { EnvironmentOutlined, SearchOutlined, CheckCircleFilled } from "@ant-design/icons";
import { getFlagUrl } from "../../utils/countriesApi";

const { Text } = Typography;

// Letters typed within this window build one search term ("gu" -> Guinea).
const TYPEAHEAD_RESET_MS = 600;

// Modifier keys must not end a search.
const MODIFIER_KEYS = ["Shift", "CapsLock", "Control", "Alt", "Meta", "AltGraph"];

export const normalize = (text: string, locale: string) =>
  text.normalize("NFD").replace(/\p{Diacritic}/gu, "").toLocaleLowerCase(locale);

const isTextEntry = (el: HTMLElement | null) => {
  if (!el) return false;
  if (el.isContentEditable) return true;
  if (el.tagName === "TEXTAREA" || el.tagName === "SELECT") return true;
  return el instanceof HTMLInputElement && !["radio", "checkbox", "button", "submit"].includes(el.type);
};

export interface TbfCountry {
  c_code: number;
  name: string;
}

interface TbfCountryListProps {
  countries: TbfCountry[];
  value: number[];
  onChange: (cCodes: number[]) => void;
  multiple?: boolean;
}

/**
 * Grid of country cards, single or multi-select. Typing letters inside the card jumps to
 * the matching country; Enter or Space selects it.
 */
export default function TbfCountryList({ countries, value, onChange, multiple = false }: TbfCountryListProps) {
  const { t, i18n } = useTranslation();
  const { token } = theme.useToken();
  const locale = i18n.language;
  const isRtl = locale === "ar";

  const [searchText, setSearchText] = useState("");

  const visible = useMemo(() => {
    const query = normalize(searchText.trim(), locale);
    return countries
      .filter((c) => !query || normalize(c.name, locale).includes(query))
      .sort((a, b) => a.name.localeCompare(b.name, locale));
  }, [countries, searchText, locale]);
  const keys = useMemo(() => visible.map((c) => normalize(c.name, locale)), [visible, locale]);
  const selected = useMemo(() => new Set(value), [value]);

  const [activeIndex, setActiveIndex] = useState(0);
  const gridRef = useRef<HTMLDivElement>(null);
  const itemRefs = useRef<(HTMLDivElement | null)[]>([]);
  const typeBuffer = useRef("");
  const resetTimer = useRef<ReturnType<typeof setTimeout>>();

  useEffect(() => { setActiveIndex(0); }, [visible]);

  const moveTo = (index: number, block: ScrollLogicalPosition = "nearest") => {
    setActiveIndex(index);
    itemRefs.current[index]?.scrollIntoView({ block, behavior: "smooth" });
  };

  // Column count depends on the breakpoint.
  const columnCount = () => {
    const firstTop = itemRefs.current[0]?.offsetTop;
    let n = 0;
    while (n < visible.length && itemRefs.current[n]?.offsetTop === firstTop) n++;
    return Math.max(n, 1);
  };

  const toggle = (cCode: number) => {
    if (!multiple) {
      onChange(selected.has(cCode) ? [] : [cCode]);
      return;
    }
    onChange(selected.has(cCode) ? value.filter((c) => c !== cCode) : [...value, cCode]);
  };

  const findFrom = (query: string, start: number) => {
    for (let k = 0; k < keys.length; k++) {
      const i = (start + k) % keys.length;
      if (keys[i].startsWith(query)) return i;
    }
    return -1;
  };

  const handleTypeahead = (char: string) => {
    const typed = normalize(char, locale);
    if (!typed) return;

    const buffer = typeBuffer.current + typed;
    const repeated = [...buffer].every((ch) => ch === buffer[0]);
    // Repeating a letter cycles through its matches.
    const query = repeated ? buffer[0] : buffer;
    const start = repeated && buffer.length > 1 ? activeIndex + 1 : 0;

    let index = findFrom(query, start);
    typeBuffer.current = buffer;
    clearTimeout(resetTimer.current);
    resetTimer.current = setTimeout(() => { typeBuffer.current = ""; }, TYPEAHEAD_RESET_MS);
    if (index === -1 && buffer.length > 1) {
      // No match for the whole run: start over from this letter.
      typeBuffer.current = typed;
      index = findFrom(typed, 0);
    }
    if (index === -1) return;

    gridRef.current?.focus({ preventScroll: true });
    moveTo(index, "center");
  };

  const endSearch = () => {
    clearTimeout(resetTimer.current);
    typeBuffer.current = "";
  };

  const handleCardKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (MODIFIER_KEYS.includes(e.key)) return;
    if (e.key.length !== 1) {
      endSearch();
      return;
    }
    if (e.defaultPrevented || e.ctrlKey || e.metaKey || e.altKey) return;
    if (isTextEntry(e.target as HTMLElement)) return;
    // Outside a search a space still scrolls the page.
    if (e.key === " " && !typeBuffer.current) return;
    e.preventDefault();
    handleTypeahead(e.key);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (!visible.length) return;
    const last = visible.length - 1;
    const next = isRtl ? "ArrowLeft" : "ArrowRight";
    const prev = isRtl ? "ArrowRight" : "ArrowLeft";

    switch (e.key) {
      case next:
        e.preventDefault();
        moveTo(Math.min(activeIndex + 1, last));
        return;
      case prev:
        e.preventDefault();
        moveTo(Math.max(activeIndex - 1, 0));
        return;
      case "ArrowDown":
        e.preventDefault();
        moveTo(Math.min(activeIndex + columnCount(), last));
        return;
      case "ArrowUp":
        e.preventDefault();
        moveTo(Math.max(activeIndex - columnCount(), 0));
        return;
      case "Home":
        e.preventDefault();
        moveTo(0);
        return;
      case "End":
        e.preventDefault();
        moveTo(last);
        return;
      case "Enter":
        e.preventDefault();
        toggle(visible[activeIndex].c_code);
        return;
      case " ":
        // Mid-search a space belongs to the name ("Burkina Faso").
        if (typeBuffer.current) return;
        e.preventDefault();
        toggle(visible[activeIndex].c_code);
        return;
      default:
    }
  };

  const activeCountry = visible[activeIndex];

  return (
    <Card
      className="tbf-home__list-card"
      onKeyDown={handleCardKeyDown}
      onPointerDown={endSearch}
      title={
        <div className="tbf-home__list-title">
          <div className="tbf-home__list-title-icon"><EnvironmentOutlined /></div>
          <div>
            <Text strong className="tbf-home__list-title-text">{t("tbf.step1")}</Text>
            <Text className="tbf-home__list-subtitle">
              {t("tbf.countries", { count: visible.length })}
              {value.length > 0 && ` · ${t("tbf.selectedCount", { count: value.length })}`}
            </Text>
          </div>
        </div>
      }
      extra={
        <div className="tbf-home__list-extra">
          {value.length > 0 && (
            <Button type="link" size="small" onClick={() => onChange([])}>
              {t("tbf.clear")}
            </Button>
          )}
          <Input
            className="tbf-home__search"
            placeholder={t("tbf.searchPlaceholder")}
            prefix={<SearchOutlined style={{ color: token.colorTextPlaceholder }} />}
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            allowClear
          />
        </div>
      }
    >
      <Text type="secondary" className="tbf-home__hint">{t(multiple ? "tbf.listHint" : "tbf.listHintSingle")}</Text>

      {countries.length === 0 ? (
        <div className="tbf-home__list-loading"><Spin /></div>
      ) : (
        <div
          ref={gridRef}
          className="tbf-home__grid"
          role="listbox"
          aria-multiselectable={multiple}
          aria-label={t("tbf.step1")}
          aria-activedescendant={activeCountry ? `tbf-country-${activeCountry.c_code}` : undefined}
          tabIndex={0}
          onKeyDown={handleKeyDown}
        >
          <Row gutter={[12, 12]}>
            {visible.map((country, i) => {
              const isSelected = selected.has(country.c_code);
              const className = [
                "tbf-home__country-card",
                i === activeIndex && "is-active",
                isSelected && "is-selected",
              ].filter(Boolean).join(" ");

              return (
                <Col xs={12} sm={8} md={8} lg={6} xl={4} key={country.c_code}>
                  <div
                    id={`tbf-country-${country.c_code}`}
                    ref={(el) => { itemRefs.current[i] = el; }}
                    role="option"
                    aria-selected={isSelected}
                    className={className}
                    style={{ animationDelay: `${Math.min(i, 12) * 25}ms` }}
                    onClick={() => {
                      setActiveIndex(i);
                      toggle(country.c_code);
                    }}
                  >
                    <img
                      src={getFlagUrl(country.c_code)}
                      alt=""
                      className="tbf-home__country-flag"
                      onError={(e) => { e.currentTarget.style.visibility = "hidden"; }}
                    />
                    <Text strong className="tbf-home__country-name">{country.name}</Text>
                    {isSelected && <CheckCircleFilled className="tbf-home__country-check" />}
                  </div>
                </Col>
              );
            })}
          </Row>
        </div>
      )}
    </Card>
  );
}
